import { useState, useMemo, useEffect, Fragment } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import {
  DownloadSimple, Sparkle, ArrowLeft, CheckCircle, XCircle, Warning, Stack,
  PlusCircle, Columns, MagicWand, CaretLeft, CaretRight, FunnelSimple, LinkSimple, LinkBreak,
} from "@phosphor-icons/react";

function StatCard({ label, value, sub, tone = "default", testid }) {
  const tones = {
    default: "text-foreground",
    match: "text-[#16A34A]",
    mismatch: "text-[#DC2626]",
    primary: "text-primary",
  };
  return (
    <div data-testid={testid} className="border border-border bg-white p-4">
      <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
      <div className={`font-heading text-3xl font-black mt-1 ${tones[tone]}`}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

export default function ResultsStep({ result: initialResult, comparisonId, name, onBack, onReport }) {
  const [result, setResult] = useState(initialResult);
  useEffect(() => setResult(initialResult), [initialResult]);
  const [filter, setFilter] = useState("all");
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [linkTarget, setLinkTarget] = useState(null); // B row awaiting an A match
  const [linkSearch, setLinkSearch] = useState("");
  const [linking, setLinking] = useState(false);
  const comparisons = result.comparisons;
  const ca = result.column_analysis || { new_columns: [], dropped_columns: [], new_col_count: 0 };

  const unmatchedA = useMemo(
    () => result.rows.filter((r) => r.origin === "old_only" || r.origin === "old_unkeyed"),
    [result]
  );

  const filteredUnmatchedA = useMemo(() => {
    const q = linkSearch.trim().toLowerCase();
    if (!q) return unmatchedA;
    return unmatchedA.filter((r) => {
      if ((r.key || "").toLowerCase().includes(q)) return true;
      return Object.values(r.cells).some(
        (c) => c.old != null && String(c.old).toLowerCase().includes(q)
      );
    });
  }, [unmatchedA, linkSearch]);

  const confirmLink = async (aRow) => {
    setLinking(true);
    try {
      const { data } = await api.post(`/comparisons/${comparisonId}/links`, {
        a_index: aRow.idx_a,
        b_index: linkTarget.idx_b,
      });
      setResult(data.result);
      toast.success("Records linked — moved to matched");
      setLinkTarget(null);
      setLinkSearch("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Link failed");
    } finally {
      setLinking(false);
    }
  };

  const unlink = async (aIndex) => {
    try {
      const { data } = await api.delete(`/comparisons/${comparisonId}/links`, {
        params: { a_index: aIndex },
      });
      setResult(data.result);
      toast.success("Link removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Unlink failed");
    }
  };

  const chartData = useMemo(
    () => result.summary.map((s) => ({ name: s.name, match: s.match_pct })),
    [result]
  );

  const rows = useMemo(() => {
    if (filter === "diff")
      return result.rows.filter((r) => Object.values(r.cells).some((c) => c.status === "mismatch" || c.status === "lost"));
    if (filter === "new") return result.rows.filter((r) => r.origin === "new_only");
    if (filter === "review") return result.rows.filter((r) => r.origin === "new_unkeyed");
    if (filter === "missing") return result.rows.filter((r) => r.origin === "old_only" || r.origin === "old_unkeyed");
    return result.rows;
  }, [result, filter]);

  useEffect(() => setPage(1), [filter, pageSize]);
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const pageRows = useMemo(
    () => rows.slice((page - 1) * pageSize, page * pageSize),
    [rows, page, pageSize]
  );
  const startIdx = rows.length === 0 ? 0 : (page - 1) * pageSize + 1;
  const endIdx = Math.min(page * pageSize, rows.length);

  const exportXlsx = async (gapsOnly = false) => {
    setExporting(true);
    try {
      const res = await api.get(`/comparisons/${comparisonId}/export`, {
        params: gapsOnly ? { gaps_only: true } : {},
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `comparison_${comparisonId.slice(0, 8)}${gapsOnly ? "_gaps" : ""}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success(gapsOnly ? "Gaps-only XLSX exported" : "XLSX exported");
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const cellClass = (status) =>
    status === "mismatch"
      ? "bg-[#FEE2E2] text-[#991B1B]"
      : status === "missing"
      ? "bg-[#FEF9C3] text-[#854D0E]"
      : status === "added"
      ? "bg-[#DBEAFE] text-[#1E40AF]"
      : status === "enriched"
      ? "bg-[#CCFBF1] text-[#0F766E]"
      : status === "lost"
      ? "bg-[#FFEDD5] text-[#9A3412]"
      : "";

  const StatusIcon = ({ status }) =>
    status === "match" ? (
      <CheckCircle size={16} weight="fill" className="text-[#16A34A]" />
    ) : status === "mismatch" ? (
      <XCircle size={16} weight="fill" className="text-[#DC2626]" />
    ) : status === "added" ? (
      <PlusCircle size={16} weight="fill" className="text-[#3B82F6]" />
    ) : status === "enriched" ? (
      <PlusCircle size={16} weight="fill" className="text-[#0D9488]" />
    ) : status === "lost" ? (
      <Warning size={16} weight="fill" className="text-[#EA580C]" />
    ) : (
      <Warning size={16} weight="fill" className="text-[#F59E0B]" />
    );

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-primary mb-2">Step 3 / 3 · Results</div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">{name}</h2>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => exportXlsx(false)} disabled={exporting} data-testid="export-xlsx-button" className="gap-2">
            <DownloadSimple size={18} /> {exporting ? "Exporting…" : "Export all"}
          </Button>
          <Button variant="outline" onClick={() => exportXlsx(true)} disabled={exporting} data-testid="export-gaps-button" className="gap-2">
            <FunnelSimple size={18} /> Export gaps
          </Button>
          <Button onClick={onReport} data-testid="generate-report-button" className="gap-2">
            <Sparkle size={18} weight="fill" /> AI Report
          </Button>
        </div>
      </div>

      {/* Coverage uplift headline */}
      <div
        data-testid="coverage-uplift"
        className="mb-6 border border-black/30 text-white p-6"
        style={{ backgroundColor: "#0F172A" }}
      >
        <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-white/60">
          <MagicWand size={16} weight="fill" className="text-sky" />
          Coverage delta · what Dataset B adds
        </div>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-6">
          <div>
            <div className="font-heading text-4xl font-black text-[#4ADE80]">
              +{result.found_new_tagged ?? 0}
            </div>
            <div className="mt-1 text-sm text-white/70">keyed records only in Dataset B</div>
          </div>
          <div>
            <div className="font-heading text-4xl font-black text-[#60A5FA]">
              +{ca.new_columns.length}
            </div>
            <div className="mt-1 text-sm text-white/70">fields only in Dataset B</div>
          </div>
          <div>
            <div className="font-heading text-4xl font-black text-white">{result.matched_records}</div>
            <div className="mt-1 text-sm text-white/70">records matched across both</div>
          </div>
          <div>
            <div className="font-heading text-4xl font-black text-[#FBBF24]">{result.needs_review_new ?? 0}</div>
            <div className="mt-1 text-sm text-white/70">unkeyed rows in B to review</div>
          </div>
        </div>
        {(result.found_new_tagged > 0 || ca.new_columns.length > 0) && (
          <p className="mt-4 text-sm text-white/60 max-w-3xl leading-relaxed">
            Dataset B contains{" "}
            <span className="text-white font-medium">{result.found_new_tagged ?? 0} keyed record(s)</span> and{" "}
            <span className="text-white font-medium">{ca.new_columns.length} field(s)</span>{" "}
            not present in Dataset A.
            {result.needs_review_new > 0 && (
              <> {result.needs_review_new} unkeyed row(s) in B can&apos;t be auto-matched and need a quick review.</>
            )}
          </p>
        )}
      </div>

      {/* Overview */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="stat-matched" label="Matched on key" value={result.matched_records} sub={`${result.total_rows_a} in A · ${result.total_rows_b} in B`} tone="primary" />
        <StatCard testid="stat-found-new" label="Only in B · keyed" value={result.found_new_tagged ?? 0} sub="new / re-keyed records" tone="match" />
        <StatCard testid="stat-needs-review" label="Needs review · unkeyed" value={result.needs_review_new ?? 0} sub="can't auto-match" tone={result.needs_review_new ? "mismatch" : "match"} />
        <StatCard testid="stat-missing" label="Only in A" value={result.missing_in_new} sub={`${result.missing_tagged ?? 0} keyed · ${result.old_unkeyed ?? 0} unkeyed`} tone={result.missing_in_new ? "mismatch" : "match"} />
      </div>

      {/* Per comparison summary */}
      <div className="mt-4 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {result.summary.map((s) => (
          <div key={s.name} data-testid={`summary-${s.name}`} className="border border-border bg-white p-4">
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold truncate">{s.name}</span>
              <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground border border-border px-1.5 py-0.5 shrink-0">{s.type}</span>
            </div>
            {(s.col_a || s.col_b) && (
              <div className="mt-1 font-mono text-[11px] text-muted-foreground truncate" title={`${s.col_a} → ${s.col_b}`}>
                {s.col_a} <span className="text-primary">→</span> {s.col_b}
              </div>
            )}
            <div className="mt-2 flex items-baseline gap-2">
              <span className={`font-heading text-4xl font-black ${s.match_pct >= 90 ? "text-[#16A34A]" : s.match_pct >= 60 ? "text-[#F59E0B]" : "text-[#DC2626]"}`}>
                {s.match_pct}%
              </span>
              <span className="text-sm text-muted-foreground">agree · {s.compared} comparable</span>
            </div>
            <div className="mt-3 h-1.5 bg-secondary">
              <div className="h-full bg-primary" style={{ width: `${s.match_pct}%` }} />
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 font-mono text-xs">
              <div><div className="text-[#16A34A] font-semibold">{s.matches}</div><div className="text-muted-foreground">match</div></div>
              <div><div className="text-[#DC2626] font-semibold">{s.mismatches}</div><div className="text-muted-foreground">conflict</div></div>
              <div><div className="text-[#0D9488] font-semibold">{s.enriched ?? 0}</div><div className="text-muted-foreground">enriched</div></div>
            </div>
            {(s.avg_delta != null || (s.lost ?? 0) > 0) && (
              <div className="mt-2 font-mono text-[11px] text-muted-foreground">
                {s.avg_delta != null && <span>avg Δ {s.avg_delta}</span>}
                {s.avg_delta != null && (s.lost ?? 0) > 0 && <span> · </span>}
                {(s.lost ?? 0) > 0 && <span>{s.lost} lost (blank in B)</span>}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Chart */}
      <div className="mt-4 border border-border bg-white p-5">
        <div className="flex items-center gap-2 mb-4">
          <Stack size={18} className="text-primary" />
          <span className="font-mono text-xs uppercase tracking-[0.2em]">Match rate by comparison</span>
        </div>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={chartData} margin={{ top: 8, right: 8, left: -16, bottom: 8 }}>
            <CartesianGrid strokeDasharray="2 2" stroke="#E4E4E7" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 12, fontFamily: "IBM Plex Mono" }} stroke="#52525B" />
            <YAxis domain={[0, 100]} tick={{ fontSize: 12, fontFamily: "IBM Plex Mono" }} stroke="#52525B" />
            <Tooltip formatter={(v) => `${v}%`} contentStyle={{ borderRadius: 0, border: "1px solid #D4D4D8", fontFamily: "IBM Plex Mono", fontSize: 12 }} />
            <Bar dataKey="match" radius={0} isAnimationActive={false}>
              {chartData.map((d) => (
                <Cell key={d.name} fill={d.match >= 90 ? "#16A34A" : d.match >= 60 ? "#F59E0B" : "#DC2626"} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Value-add columns */}
      {(ca.new_columns.length > 0 || ca.dropped_columns.length > 0) && (
        <div className="mt-4 border border-border bg-white" data-testid="value-add-panel">
          <div className="flex items-center gap-2 px-5 py-3 border-b border-border bg-secondary">
            <MagicWand size={18} weight="fill" className="text-primary" />
            <span className="font-mono text-xs uppercase tracking-[0.2em]">
              Value-add fields — present only in Dataset B
            </span>
          </div>
          <div className="p-5">
            {ca.new_columns.length > 0 ? (
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                {ca.new_columns.map((col) => (
                  <div key={col.name} data-testid={`new-field-${col.name}`} className="border border-[#BFDBFE] bg-[#EFF6FF] p-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-[#1E40AF] truncate flex items-center gap-1.5">
                        <PlusCircle size={15} weight="fill" /> {col.name}
                      </span>
                      {col.mapped && (
                        <span className="font-mono text-[9px] uppercase tracking-wider bg-primary text-white px-1.5 py-0.5">mapped</span>
                      )}
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-white border border-[#BFDBFE]">
                        <div className="h-full bg-[#3B82F6]" style={{ width: `${col.fill_rate}%` }} />
                      </div>
                      <span className="font-mono text-xs text-[#1E40AF] font-semibold">{col.fill_rate}%</span>
                    </div>
                    <div className="mt-1 font-mono text-[10px] text-muted-foreground">populated</div>
                    {col.samples.length > 0 && (
                      <div className="mt-2 font-mono text-[10px] text-muted-foreground truncate">
                        e.g. {col.samples.join(" · ")}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No new fields detected in Dataset B.</p>
            )}
            {ca.dropped_columns.length > 0 && (
              <div className="mt-4 pt-4 border-t border-border">
                <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-2 flex items-center gap-1.5">
                  <Columns size={14} /> Fields only in Dataset A ({ca.dropped_columns.length})
                </div>
                <div className="flex flex-wrap gap-2">
                  {ca.dropped_columns.map((col) => (
                    <span key={col.name} className="font-mono text-xs border border-border px-2 py-1 text-muted-foreground">
                      {col.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="mt-4 border border-border bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-b border-border bg-secondary">
          <span className="font-mono text-xs uppercase tracking-[0.2em]">Comparison table ({rows.length} rows)</span>
          <div className="flex items-center gap-1" data-testid="row-filter">
            {[
              { id: "all", label: "All" },
              { id: "diff", label: "Conflicts" },
              { id: "new", label: "Only in B" },
              { id: "review", label: "Needs review" },
              { id: "missing", label: "Only in A" },
            ].map((f) => (
              <button
                key={f.id}
                data-testid={`filter-${f.id}`}
                onClick={() => setFilter(f.id)}
                className={`font-mono text-[11px] uppercase tracking-wider px-2.5 py-1 border transition-colors ${
                  filter === f.id
                    ? "bg-primary text-white border-primary"
                    : "bg-white text-muted-foreground border-border hover:border-primary/50"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-auto max-h-[560px]">
          <table className="w-full border-collapse text-sm" data-testid="comparison-table">
            <thead className="sticky top-0 z-10">
              <tr className="bg-foreground text-white">
                <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-wider border-r border-white/20 sticky left-0 bg-foreground">Key</th>
                {comparisons.map((c) => (
                  <th key={c.name} colSpan={2} className="text-left px-3 py-2 font-mono text-xs uppercase tracking-wider border-r border-white/20">
                    {c.name}
                    {(c.col_a || c.col_b) && (
                      <div className="text-[9px] normal-case tracking-normal text-white/50 mt-0.5">
                        {c.col_a} → {c.col_b}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
              <tr className="bg-[#3f3f46] text-white/80">
                <th className="px-3 py-1.5 border-r border-white/10 sticky left-0 bg-[#3f3f46]"></th>
                {comparisons.map((c) => (
                  <Fragment key={c.name}>
                    <th className="text-left px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider">Dataset A</th>
                    <th className="text-left px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider border-r border-white/10">Dataset B</th>
                  </Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {pageRows.map((r) => {
                const rowBg =
                  r.origin === "new_only" ? "bg-[#EFF6FF]"
                  : r.origin === "new_unkeyed" ? "bg-[#FFF7ED]"
                  : r.origin === "old_only" ? "bg-[#FEFCE8]"
                  : r.origin === "old_unkeyed" ? "bg-[#F4F4F5]"
                  : "bg-white";
                return (
                <tr key={`${r.origin}-${r.idx_a}-${r.idx_b}-${r.key}`} className={`border-b border-border hover:bg-secondary/60 transition-colors ${r.origin !== "both" ? rowBg + "/50" : ""}`}>
                  <td className={`px-3 py-1.5 font-mono text-xs font-medium border-r border-border sticky left-0 ${rowBg}`}>
                    <span className="flex items-center gap-1.5">
                      {r.key || "—"}
                      {r.origin === "new_only" && (
                        <span className="font-mono text-[9px] uppercase tracking-wider bg-[#3B82F6] text-white px-1 py-0.5">b only</span>
                      )}
                      {r.origin === "new_unkeyed" && (
                        <span className="font-mono text-[9px] uppercase tracking-wider bg-[#EA580C] text-white px-1 py-0.5">review</span>
                      )}
                      {r.origin === "old_only" && (
                        <span className="font-mono text-[9px] uppercase tracking-wider bg-[#F59E0B] text-white px-1 py-0.5">a only</span>
                      )}
                      {r.origin === "old_unkeyed" && (
                        <span className="font-mono text-[9px] uppercase tracking-wider bg-[#71717A] text-white px-1 py-0.5">a · no key</span>
                      )}
                      {r.manual_link && (
                        <span className="font-mono text-[9px] uppercase tracking-wider bg-[#7C3AED] text-white px-1 py-0.5 flex items-center gap-0.5">
                          <LinkSimple size={9} weight="bold" /> linked
                        </span>
                      )}
                      {comparisonId && (r.origin === "new_only" || r.origin === "new_unkeyed") && (
                        <button
                          data-testid={`link-row-${r.idx_b}`}
                          onClick={() => { setLinkTarget(r); setLinkSearch(""); }}
                          className="ml-1 font-mono text-[9px] uppercase tracking-wider border border-[#7C3AED] text-[#7C3AED] hover:bg-[#7C3AED] hover:text-white transition-colors px-1 py-0.5 flex items-center gap-0.5"
                          title="Link this row to an unmatched record in Dataset A"
                        >
                          <LinkSimple size={9} weight="bold" /> link
                        </button>
                      )}
                      {comparisonId && r.manual_link && (
                        <button
                          data-testid={`unlink-row-${r.idx_a}`}
                          onClick={() => unlink(r.idx_a)}
                          className="ml-1 text-muted-foreground hover:text-destructive transition-colors"
                          title="Unlink"
                        >
                          <LinkBreak size={13} />
                        </button>
                      )}
                    </span>
                  </td>
                  {comparisons.map((c) => {
                    const cell = r.cells[c.name];
                    return (
                      <Fragment key={c.name}>
                        <td className={`px-3 py-1.5 font-mono text-xs ${cellClass(cell.status)}`}>
                          {cell.old == null ? "—" : String(cell.old)}
                        </td>
                        <td className={`px-3 py-1.5 font-mono text-xs border-r border-border ${cellClass(cell.status)}`}>
                          <span className="flex items-center gap-1.5">
                            <StatusIcon status={cell.status} />
                            {cell.status === "missing" ? "—" : String(cell.new ?? "")}
                            {cell.delta != null && c.type === "numeric" && (
                              <span className="text-[10px] text-muted-foreground">(Δ{cell.delta > 0 ? "+" : ""}{cell.delta})</span>
                            )}
                          </span>
                        </td>
                      </Fragment>
                    );
                  })}
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {rows.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-t border-border bg-secondary">
            <div className="flex items-center gap-2 font-mono text-xs text-muted-foreground">
              <span>Rows per page</span>
              <select
                data-testid="page-size-select"
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="border border-border bg-white px-2 py-1 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {[50, 100, 250, 500].map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
              <span className="ml-2">{startIdx}–{endIdx} of {rows.length}</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline" size="sm" data-testid="page-prev"
                disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="gap-1"
              >
                <CaretLeft size={14} /> Prev
              </Button>
              <span className="font-mono text-xs text-muted-foreground">Page {page} / {totalPages}</span>
              <Button
                variant="outline" size="sm" data-testid="page-next"
                disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="gap-1"
              >
                Next <CaretRight size={14} />
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="mt-6">
        <Button variant="ghost" onClick={onBack} data-testid="results-back-button" className="gap-2">
          <ArrowLeft size={18} /> Edit mapping
        </Button>
      </div>

      {/* Manual link picker */}
      <Dialog open={!!linkTarget} onOpenChange={(o) => !o && setLinkTarget(null)}>
        <DialogContent data-testid="link-picker-dialog" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LinkSimple size={18} weight="bold" className="text-[#7C3AED]" />
              Link to a record in Dataset A
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Choose the unmatched Dataset A record that is actually the same as{" "}
              <span className="font-mono font-medium text-foreground">
                {linkTarget?.key ? `“${linkTarget.key}”` : "this unkeyed row"}
              </span>
              . They&apos;ll be merged into a single matched record.
            </p>
            <Input
              data-testid="link-search-input"
              autoFocus
              placeholder="Search unmatched Dataset A rows…"
              value={linkSearch}
              onChange={(e) => setLinkSearch(e.target.value)}
            />
            <div className="border border-border max-h-80 overflow-auto" data-testid="link-candidates">
              {filteredUnmatchedA.length === 0 ? (
                <div className="p-6 text-center text-sm text-muted-foreground">
                  No unmatched Dataset A records{unmatchedA.length ? " match your search" : " available to link"}.
                </div>
              ) : (
                filteredUnmatchedA.map((aRow) => (
                  <button
                    key={aRow.idx_a}
                    data-testid={`link-candidate-${aRow.idx_a}`}
                    onClick={() => confirmLink(aRow)}
                    disabled={linking}
                    className="w-full text-left px-4 py-2.5 border-b border-border last:border-b-0 hover:bg-secondary transition-colors disabled:opacity-50"
                  >
                    <div className="font-mono text-sm font-medium">{aRow.key || "— (no key)"}</div>
                    <div className="font-mono text-[11px] text-muted-foreground truncate mt-0.5">
                      {comparisons
                        .map((c) => {
                          const v = aRow.cells[c.name]?.old;
                          return v == null || v === "" ? null : `${c.name}: ${v}`;
                        })
                        .filter(Boolean)
                        .join("  ·  ") || "no comparison values"}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
